<template>
  <div class="symptom-details">
    <filters-section :showLocation="true" :includeUnits="false" :showCurrentLocationToggle="true"
      :filterDisplayKeys="getFilterKeys()" :moreFilters="global.additionalFilters"
      :dateRangeStart="global.defaultDateStart" v-on:change="formatFilters">
    </filters-section>
    <page-section title="Summary" :set="tableGridColumns = getTableColumns()">
      <TableGrid :columns="tableGridColumns" :rows="summarySection.data">
        <template slot="columns" slot-scope="scope">
          <div v-for="col in tableGridColumns">
            {{ scope.row[col.key] || "Loading..." }}
          </div>
        </template>
      </TableGrid>
    </page-section>
  </div>
</template>

<script>
import moment from "moment";
import auth from "@/auth";
import FiltersSection from "../../Shared/FiltersSection";
import PageSection from "../../Shared/PageSection";
import TableGrid from "@/components/Shared/TableGrid.vue";

export default {
  name: "symptom-detail-page",
  components: {
    "filters-section": FiltersSection,
    "page-section": PageSection,
    TableGrid
  },
  data() {
    return {
      global: {
        filters: {},
        defaultDateStart: moment()
          .subtract(90, "days")
          .toDate(),
        sites: [],
        facilities: [],
        additionalFilters: [],
      },
      summarySection: {
        loading: true,
        data: [],
        columns: [],
      },
      requestController: null,
    };
  },
  methods: {
    getFilterKeys() {
      return [];
    },
    getTableColumns() {
      const cols = [
        { key: 'facility', label: 'Facility', width: 'max-content' },
        { key: 'infectionCreated', label: 'Infection Case Created', width: '1fr' },
        { key: 'infectionUpdated', label: 'Infection Case Updated', width: '1fr' },
      ];
      if (this.hasHandHygiene()) {
        cols.push({ key: 'hhCreated', label: 'HH Created', width: '1fr' })
      }
      if (this.hasPPE()) {
        cols.push({ key: 'ppeCreated', label: 'PPE Created', width: '1fr' })
      }
      return cols;
    },
    formatFilters(rawFilters) {
      const filterValues = Object.fromEntries(
        Object.entries(rawFilters)
          .filter(([k, v]) => v !== 0)
          .map(([k, v]) => {
            switch (k) {
              case "facilities":
                k = "facIds";
                break;
            }
            return [k, v];
          })
      );
      this.global.filters = { ...filterValues };
      this.getPerFacilityData();
    },
    abortWith(signal) {
      // vue-resource sets request.abort immediately before it sends the request.
      return {
        before(request) {
          signal.addEventListener("abort", () => request.abort(), { once: true });
        },
      };
    },
    ignoreAbort(signal) {
      return error => {
        if (!signal.aborted) {
          throw error;
        }
      };
    },
    hasPPE: function () {
      return auth.userInfo().roles.indexOf("PPE_READ") >= 0;
    },
    hasHandHygiene() {
      return auth.userInfo().roles.indexOf("ROLE_HH_OBSERVER");
    },
    async getPerFacilityData() {
      // Cancel the requests of the previous filter selection. Their responses
      // are stale and they make the table flicker.
      if (this.requestController) {
        this.requestController.abort();
      }
      this.requestController = new AbortController();
      const signal = this.requestController.signal;
      const abortable = this.abortWith(signal);
      const ignoreAbort = this.ignoreAbort(signal);
      const filteredFacilities = this.global.facilities.filter(({ id }) => this.global.filters.facIds.includes(id));
      const summaryFacilityLookup = new Map(filteredFacilities.map(({ id, displayText }) => [id, {
        "facility": displayText,
        "infectionCreated": null,
        "infectionUpdated": null,
        "hhCreated": null,
        "ppeCreated": null,
      }]))
      const baseFilter = {
        page: 1,
        size: 1
      };
      const infectionCreatedFilters = {
        ...baseFilter,
        useCurrentLocation: false,
        createDateFrom: moment()
          .subtract(6, "months")
          .format(),
        createDateTo: moment().format(),
        orderBy: "created",
      };
      const infectionUpdatedFilters = {
        ...baseFilter,
        useCurrentLocation: false,
        updateDateFrom: moment()
          .subtract(6, "months")
          .format(),
        updateDateTo: moment().format(),
        orderBy: "updated",
      };
      const ppeFilters = {
        ...baseFilter,
        filters: {
          facilityIds: [],
        }
      };
      const recordedLocalDatetimeStart = moment().subtract(6, "months").format();
      const hhFilters = {
        ...baseFilter,
        start: moment().subtract(6, "months").format(),
        end: moment().format()
      };

      this.summarySection.data = [...summaryFacilityLookup.values()];

      filteredFacilities.forEach(({ id }) => {

        this.$http.post(`${window.CONFIG.infection_api}/search`, { ...infectionCreatedFilters, facIds: [id] }, abortable)
          .then(resp => resp.json())
          .then((data) => {
            if (signal.aborted) return;
            const infectionCreated = data.content[0]?.created ? moment(data.content[0].created).format(this.$configStore.dateFormat()) : "No records in the last 6 months"
            summaryFacilityLookup.set(id, { ...summaryFacilityLookup.get(id), infectionCreated });
            this.summarySection.data = [...summaryFacilityLookup.values()];
          })
          .catch(ignoreAbort)

        this.$http.post(`${window.CONFIG.infection_api}/search`, { ...infectionUpdatedFilters, facIds: [id] }, abortable)
          .then(resp => resp.json())
          .then((data) => {
            if (signal.aborted) return;
            const infectionUpdated = data.content[0]?.updated ? moment(data.content[0].updated).format(this.$configStore.dateFormat()) : "No records in the last 6 months"
            summaryFacilityLookup.set(id, { ...summaryFacilityLookup.get(id), infectionUpdated });
            this.summarySection.data = [...summaryFacilityLookup.values()];
          })
          .catch(ignoreAbort)

        if (this.hasPPE()) {
          this.$http.post(`${window.CONFIG.ppe_api}/audit/search`, { ...ppeFilters, filters: { facilityIds: [id], recordedLocalDatetimeStart } }, abortable)
            .then(resp => resp.json())
            .then((data) => {
              if (signal.aborted) return;
              const ppeCreated = data.records[0]?.createdTimestamp ? moment(data.records[0].createdTimestamp).format(this.$configStore.dateFormat()) : "No records in the last 6 months"
              summaryFacilityLookup.set(id, { ...summaryFacilityLookup.get(id), ppeCreated });
              this.summarySection.data = [...summaryFacilityLookup.values()];
            })
            .catch(ignoreAbort);
        }
        if (this.hasHandHygiene()) {
          this.$http.get(`${window.CONFIG.hh_api}/observations`, { ...abortable, params: { ...baseFilter, ...hhFilters, facilityIds: [id] } })
            .then(response => response.body)
            .then((data) => {
              if (signal.aborted) return;
              const hhCreated = data.content[0]?.created ? moment(data.content[0].created).format(this.$configStore.dateFormat()) : "No records in the last 6 months"
              summaryFacilityLookup.set(id, { ...summaryFacilityLookup.get(id), hhCreated });
              this.summarySection.data = [...summaryFacilityLookup.values()];
            })
            .catch(ignoreAbort);
        }

      });
    },
  },
  async created() {
    this.global.facilities = this.$configStore.data.sites;
  },
  beforeDestroy() {
    if (this.requestController) {
      this.requestController.abort();
    }
  }
};
</script>

<style>
.symptom-details {
  --status-missed: #ff5050;
  --status-complete: #00d560;
  --status-incomplete: #ffcc00;
}
</style>